import CustomTooltip from '../../shared/CustomTooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import CloseIcon from '@mui/icons-material/Close';
import {
    Alert,
    Box,
    Button,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputLabel,
    Stack,
    Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import {
    useCreateDeviceMutation,
    useGetIndividualOwnerAssetsQuery,
    useRemoveDeviceMutation,
    useUpdateDeviceMutation,
} from '../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../shared/AuthRoles';
import FormAutocomplete from '../../shared/FormAutocomplete';
import FormSelect from '../../shared/FormSelect';
import FormTextField from '../../shared/FormTextField';
import DeviceSetupInstructions from '../../shared/DeviceSetupInstructions';
import toast from '../../shared/Toast';
import { ModalProps } from '../../types/modal';
import { Device } from '../../types/vehicles';
import { HelpOutline } from '@mui/icons-material';

const Schema = z.object({
    uniqueId: z.string().min(1, { message: 'Unique identifier is required' }),
    module: z.object({
        id: z.number().min(1, 'Experience is required'),
    }),
    enabled: z.boolean().default(true),
});

type DeviceFormValues = z.infer<typeof Schema>;

interface DeviceModalProps extends ModalProps {
    selectedDevice?: Device;
}

const DeviceModal = ({ selectedDevice, open, onClose }: DeviceModalProps) => {
    const loggedInUser = UserRole();
    const [createDevice] = useCreateDeviceMutation();
    const [updateDevice] = useUpdateDeviceMutation();
    const [removeDevice, { isLoading: isRemoving }] = useRemoveDeviceMutation();
    const [confirmClose, setConfirmClose] = useState(false);

    const methods = useForm<DeviceFormValues>({
        defaultValues: {
            uniqueId: '',
            module: { id: undefined },
            enabled: true,
        },
        resolver: zodResolver(Schema),
    });

    const {
        control,
        reset,
        handleSubmit,
        formState: { isDirty, isSubmitting },
    } = methods;

    useEffect(() => {
        if (selectedDevice) {
            reset(selectedDevice);
        } else {
            reset();
        }
    }, [selectedDevice]);

    const handleClose = () => {
        if (isDirty && !confirmClose) {
            setConfirmClose(true);
            return;
        }

        if (confirmClose) reset();
        setConfirmClose(false);
        onClose();
    };

    const onSubmit = async (data: DeviceFormValues) => {
        try {
            const name = vehicles?.find(
                (vehicle: any) => vehicle.id === data.module.id,
            )?.name;
            if (!name) return toast.error('Vehicle not found');
            const body = {
                ...data,
                name,
            };
            if (selectedDevice) {
                await updateDevice([selectedDevice.id, body]).unwrap();
            } else {
                await createDevice(body).unwrap();
            }
            toast.success('Device added successfully');
            reset();
            onClose();
        } catch (error) {
            toast.error('Error adding the device');
        }
    };

    const { data: vehicles, isFetching } = useGetIndividualOwnerAssetsQuery({
        url: `owners/${loggedInUser?.company}/modules`,
        type: 'vehicle',
        sort: 'name',
        status: 'published',
    });

    const vehicleOptions = vehicles?.map((vehicle: any) => ({
        label: (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 0.5,
                    width: '100%',
                }}
            >
                <Box
                    component='img'
                    src={vehicle.photos[0]?.src}
                    sx={{
                        width: 48,
                        height: 48,
                        borderRadius: 1,
                        objectFit: 'cover',
                    }}
                />
                <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: '14px' }}>
                        {vehicle.name} #{vehicle.id}
                    </Typography>
                </Box>
            </Box>
        ),
        value: vehicle.id,
    }));

    const handleRemoveDevice = async (id: number) => {
        try {
            await removeDevice(id).unwrap();
            toast.success('Device removed successfully');
            reset();
            onClose();
        } catch (error) {
            toast.error('Error removing the device');
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogTitle>
                        {selectedDevice
                            ? 'Update GPS Device'
                            : 'Add GPS Device'}
                        <IconButton
                            onClick={handleClose}
                            size='small'
                            aria-label='close'
                            sx={{ color: 'text.primary' }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent>
                        <Stack spacing={2}>
                            <Collapse in={confirmClose}>
                                <Alert severity='warning'>
                                    You have unsaved changes. Click 'Cancel'
                                    again to discard them.
                                </Alert>
                            </Collapse>

                            <Box>
                                <InputLabel required>
                                    Unique Identifier
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                The ID from your GPS provider
                                                that links this vehicle to its
                                                device.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutline
                                            sx={{
                                                fontSize: '16px',
                                                ml: 0.3,
                                                mb: 0.3,
                                            }}
                                        />
                                    </CustomTooltip>
                                </InputLabel>
                                <FormTextField
                                    name='uniqueId'
                                    control={control}
                                />
                            </Box>
                            <Box>
                                <InputLabel required>
                                    Status{' '}
                                    <CustomTooltip
                                        title={
                                            <Typography>
                                                When disabled, guests cannot see
                                                live tracking. As the owner, you
                                                can still track the vehicle.
                                            </Typography>
                                        }
                                        placement='right'
                                    >
                                        <HelpOutline
                                            sx={{
                                                fontSize: '16px',
                                                ml: 0.3,
                                                mb: 0.3,
                                            }}
                                        />
                                    </CustomTooltip>
                                </InputLabel>
                                <FormAutocomplete
                                    name='enabled'
                                    control={control}
                                    options={[
                                        { value: true, label: 'Enabled' },
                                        { value: false, label: 'Disabled' },
                                    ]}
                                />
                            </Box>

                            <Box>
                                <InputLabel required>Vehicle</InputLabel>
                                <FormSelect
                                    name='module.id'
                                    control={control}
                                    options={vehicleOptions}
                                />
                            </Box>

                            <DeviceSetupInstructions />
                        </Stack>
                    </DialogContent>

                    <DialogActions
                        sx={{
                            px: 3,
                            pb: 2,
                            display: 'flex',
                            justifyContent: selectedDevice
                                ? 'space-between'
                                : 'flex-end',
                            gap: 1,
                        }}
                    >
                        <Button
                            variant='outlined'
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Box
                            sx={{
                                display: 'flex',
                                gap: 1,
                            }}
                        >
                            {selectedDevice && (
                                <Button
                                    variant='contained'
                                    color='warning'
                                    onClick={() =>
                                        handleRemoveDevice(selectedDevice?.id)
                                    }
                                    disabled={isRemoving}
                                >
                                    Remove Device
                                </Button>
                            )}
                            <Button
                                variant='contained'
                                type='submit'
                                disabled={isSubmitting || !isDirty}
                            >
                                {selectedDevice
                                    ? 'Update Device'
                                    : 'Add Device'}
                            </Button>
                        </Box>
                    </DialogActions>
                </form>
            </FormProvider>
        </Dialog>
    );
};

export default DeviceModal;
